import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CircleAlert, CircleCheck, Loader2, Mail, RotateCcw, Send } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { PasswordInput } from '@/components/password-input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { api, ApiError } from '@/lib/api'
import { ukDate } from '@/lib/quotes'
import type { StaffUser } from '@/lib/session'
import { reportError } from '@/lib/settings'
import { FormField, NativeSelect, SettingsCard, TextInput } from './form-kit'
import { PageTitle } from './pages'

interface Account {
  host: string
  port: number
  security: 'ssl' | 'starttls'
  username: string
  hasPassword: boolean
  fromName: string
  fromEmail: string
  replyTo: string
  enabled: boolean
  verifiedAt: string | null
  lastTestError: string
  version: number
}
interface OutboxRow {
  id: string
  template: string
  to: string
  subject: string
  status: 'pending' | 'sent' | 'failed' | 'dead'
  attempts: number
  lastError: string
  sentAt: string | null
  createdAt: string
}

const PRESETS = [
  {
    key: 'stackcp',
    label: 'UK2 StackCP (hcdc.biz mailboxes)',
    host: 'smtp.stackmail.com',
    port: 465,
    security: 'ssl' as const,
  },
  {
    key: 'uk2',
    label: 'UK2 classic mail',
    host: 'mail.uk2.net',
    port: 465,
    security: 'ssl' as const,
  },
  { key: 'other', label: 'Another provider', host: '', port: 587, security: 'starttls' as const },
]
const PORTS = [
  [465, 'SSL/TLS'],
  [587, 'STARTTLS'],
  [25, 'Standard (25)'],
  [2525, 'Alternative (2525)'],
] as const

const STATUS: Record<OutboxRow['status'], [string, string]> = {
  sent: ['Sent', 'bg-success/10 text-[#0f5c3b] border-success/30 dark:text-success'],
  pending: ['Waiting', 'bg-secondary text-secondary-foreground border-transparent'],
  failed: [
    'Failed',
    'bg-destructive/10 text-[#9a2f0c] border-destructive/25 dark:text-destructive',
  ],
  dead: [
    'Not sent',
    'bg-destructive/10 text-[#9a2f0c] border-destructive/25 dark:text-destructive',
  ],
}

export function EmailSettingsPage({ me }: { me: StaffUser }) {
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['mail'],
    queryFn: () =>
      api.get<{ account: Account; recent: OutboxRow[]; encryptionReady: boolean }>('/mail'),
  })
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['mail'] })
    void qc.invalidateQueries({ queryKey: ['settings'] })
  }
  const [f, setF] = useState<(Account & { password: string }) | null>(null)
  const [testTo, setTestTo] = useState(me.email)
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null)
  useEffect(() => {
    if (q.data) setF({ ...q.data.account, password: '' })
  }, [q.data])

  const save = useMutation({
    mutationFn: (a: Account & { password: string }) =>
      api.put<Account>('/mail', {
        host: a.host,
        port: a.port,
        security: a.security,
        username: a.username,
        ...(a.password ? { password: a.password } : {}),
        fromName: a.fromName,
        fromEmail: a.fromEmail,
        replyTo: a.replyTo,
        expectedVersion: a.version,
      }),
    onSuccess: (a) => {
      refresh()
      setTestResult(null)
      toast.success(a.verifiedAt ? 'Email settings saved' : 'Saved. Now send a test email.')
    },
    onError: (e) => reportError(e, refresh),
  })
  const test = useMutation({
    mutationFn: () => api.post<{ ok: boolean; error?: string }>('/mail/test', { to: testTo }),
    onSuccess: (r) => {
      setTestResult(r)
      refresh()
      if (r.ok) toast.success(`Test email sent to ${testTo}`)
    },
    onError: (e) =>
      setTestResult({
        ok: false,
        error: e instanceof ApiError ? e.message : 'The test could not run.',
      }),
  })
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.post<Account>('/mail/enabled', { enabled }),
    onSuccess: (a) => {
      refresh()
      toast.success(a.enabled ? 'Email sending is on' : 'Email sending is off')
    },
    onError: (e) => reportError(e, refresh),
  })
  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/mail/outbox/${id}/retry`),
    onSuccess: () => {
      toast.success('Trying again')
      setTimeout(refresh, 1500)
    },
    onError: (e) => reportError(e),
  })

  if (q.isError)
    return (
      <p role="alert" className="text-destructive">
        {q.error.message}
      </p>
    )
  if (!q.data || !f) return <Loader2 className="animate-spin" aria-label="Loading" />
  const a = q.data.account
  const set = (patch: Partial<typeof f>) => setF({ ...f, ...patch })
  const preset = PRESETS.find((p) => p.host === f.host)?.key ?? 'other'
  const dirty =
    f.host !== a.host ||
    f.port !== a.port ||
    f.security !== a.security ||
    f.username !== a.username ||
    !!f.password ||
    f.fromName !== a.fromName ||
    f.fromEmail !== a.fromEmail ||
    f.replyTo !== a.replyTo
  const errors: Record<string, string> = {}
  if (!/^\S+@\S+\.\S+$/.test(f.username))
    errors.username = 'Enter the full email address, e.g. quotes@hcdc.biz'
  if (!a.hasPassword && !f.password) errors.password = 'Enter the mailbox password.'
  if (!f.host.trim()) errors.host = 'Enter the mail server.'
  if (!f.fromName.trim()) errors.fromName = 'Enter a sender name.'
  const valid = Object.keys(errors).length === 0
  const state = !a.hasPassword ? 'unset' : a.enabled ? 'on' : a.verifiedAt ? 'tested' : 'untested'

  return (
    <>
      <PageTitle
        title="Email"
        description="The mailbox HCD Portal sends from: approval requests, proposals to clients, and confirmations."
      />

      <div
        role="status"
        className={
          state === 'on'
            ? 'mb-5 flex items-start gap-3 rounded-xl border border-success/40 bg-success/10 px-4 py-3'
            : 'mb-5 flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3'
        }
      >
        {state === 'on' ? (
          <CircleCheck className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
        ) : (
          <Mail className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden />
        )}
        <div className="text-sm">
          <p className="font-semibold">
            {state === 'on' && `Emails are being sent from ${a.fromEmail || a.username}`}
            {state === 'tested' && 'Tested and ready. Switch sending on below.'}
            {state === 'untested' && 'Saved. Send a test email to check it works.'}
            {state === 'unset' && 'Email is not set up yet'}
          </p>
          <p className="mt-0.5 text-muted-foreground">
            {state === 'on'
              ? 'Approval requests, client proposals and confirmations go out automatically.'
              : 'Until sending is on, nothing is emailed. Quote links can still be copied and sent by hand.'}
          </p>
        </div>
      </div>

      {!q.data.encryptionReady && (
        <p
          role="alert"
          className="mb-5 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm"
        >
          <CircleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          The server is missing its encryption key, so a password cannot be stored safely yet.
        </p>
      )}

      <form
        className="grid gap-5"
        onSubmit={(e) => {
          e.preventDefault()
          if (valid) save.mutate(f)
        }}
      >
        <SettingsCard
          title="Mail server"
          description="HCD’s mailboxes are hosted by UK2 on StackCP, so that is already chosen."
        >
          <FormField label="Provider">
            {(p) => (
              <NativeSelect
                {...p}
                value={preset}
                onChange={(e) => {
                  const pr = PRESETS.find((x) => x.key === e.target.value)!
                  set(
                    pr.key === 'other'
                      ? { host: '', port: 587, security: 'starttls' }
                      : { host: pr.host, port: pr.port, security: pr.security },
                  )
                }}
              >
                {PRESETS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </FormField>
          <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_12rem]">
            <FormField label="Outgoing mail server (SMTP)" error={errors.host}>
              {(p) => (
                <TextInput
                  {...p}
                  value={f.host}
                  autoCapitalize="none"
                  spellCheck={false}
                  onChange={(e) => set({ host: e.target.value.trim() })}
                />
              )}
            </FormField>
            <FormField label="Port and security">
              {(p) => (
                <NativeSelect
                  {...p}
                  value={String(f.port)}
                  onChange={(e) =>
                    set({
                      port: Number(e.target.value),
                      security: e.target.value === '465' ? 'ssl' : 'starttls',
                    })
                  }
                >
                  {PORTS.map(([port, label]) => (
                    <option key={port} value={port}>
                      {port} · {label}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </FormField>
          </div>
        </SettingsCard>

        <SettingsCard
          title="Mailbox"
          description="The email address and password you use to log in to this mailbox."
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <FormField label="Email address" error={errors.username}>
              {(p) => (
                <TextInput
                  {...p}
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  value={f.username}
                  onChange={(e) => set({ username: e.target.value.trim() })}
                  placeholder="quotes@hcdc.biz"
                />
              )}
            </FormField>
            <FormField
              label="Password"
              hint={
                a.hasPassword
                  ? 'Saved securely. Leave blank to keep it.'
                  : 'Stored encrypted. It is never shown again.'
              }
              error={errors.password}
            >
              {(p) => (
                <PasswordInput
                  {...p}
                  autoComplete="new-password"
                  value={f.password}
                  placeholder={a.hasPassword ? '••••••••••' : ''}
                  onChange={(e) => set({ password: e.target.value })}
                />
              )}
            </FormField>
          </div>
        </SettingsCard>

        <SettingsCard title="From" description="How emails appear in people’s inboxes.">
          <div className="grid gap-5 sm:grid-cols-3">
            <FormField label="From name" hint="e.g. HCD Cleaning" error={errors.fromName}>
              {(p) => (
                <TextInput
                  {...p}
                  value={f.fromName}
                  onChange={(e) => set({ fromName: e.target.value })}
                />
              )}
            </FormField>
            <FormField label="From address" hint="Leave blank to use the mailbox address.">
              {(p) => (
                <TextInput
                  {...p}
                  type="email"
                  inputMode="email"
                  autoCapitalize="none"
                  value={f.fromEmail}
                  placeholder={f.username}
                  onChange={(e) => set({ fromEmail: e.target.value.trim() })}
                />
              )}
            </FormField>
            <FormField label="Replies go to" hint="Optional, e.g. office@hcdc.biz">
              {(p) => (
                <TextInput
                  {...p}
                  type="email"
                  inputMode="email"
                  autoCapitalize="none"
                  value={f.replyTo}
                  onChange={(e) => set({ replyTo: e.target.value.trim() })}
                />
              )}
            </FormField>
          </div>
          <div className="flex justify-end">
            <Button type="submit" className="h-10" disabled={!dirty || !valid || save.isPending}>
              {save.isPending && <Loader2 className="animate-spin" />}
              Save email settings
            </Button>
          </div>
        </SettingsCard>
      </form>

      <div className="mt-5 grid gap-5">
        <SettingsCard title="Test and switch on">
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
            <FormField label="Send a test email to">
              {(p) => (
                <TextInput
                  {...p}
                  type="email"
                  inputMode="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value.trim())}
                />
              )}
            </FormField>
            <Button
              className="h-11 md:h-10"
              disabled={!a.hasPassword || dirty || test.isPending || !/^\S+@\S+\.\S+$/.test(testTo)}
              onClick={() => test.mutate()}
            >
              {test.isPending ? <Loader2 className="animate-spin" /> : <Send />}
              Send test email
            </Button>
          </div>
          {dirty && (
            <p className="text-[13px] text-muted-foreground">Save your changes before testing.</p>
          )}
          {testResult && (
            <p
              role="alert"
              className={
                testResult.ok
                  ? 'flex items-start gap-2 text-sm font-medium text-[#0f5c3b] dark:text-success'
                  : 'flex items-start gap-2 text-sm font-medium text-destructive'
              }
            >
              {testResult.ok ? (
                <CircleCheck className="mt-0.5 size-4 shrink-0" aria-hidden />
              ) : (
                <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
              )}
              {testResult.ok
                ? `Sent. Check the inbox of ${testTo}; it may take a minute.`
                : testResult.error}
            </p>
          )}
          {!testResult && a.lastTestError && (
            <p className="text-sm text-destructive">Last test: {a.lastTestError}</p>
          )}
          {a.verifiedAt && (
            <p className="text-[13px] text-muted-foreground">
              Last successful test {ukDate(a.verifiedAt, true)}.
            </p>
          )}
          <label className="flex items-start justify-between gap-4 rounded-lg border p-4">
            <span>
              <span className="block text-sm font-medium">Send emails from HCD Portal</span>
              <span className="block text-[13px] text-muted-foreground">
                {a.verifiedAt
                  ? 'Approval requests, proposals and confirmations will be emailed.'
                  : 'Available after a successful test.'}
              </span>
            </span>
            <Switch
              checked={a.enabled}
              disabled={!a.verifiedAt || toggle.isPending}
              onCheckedChange={(v) => toggle.mutate(v)}
              aria-label="Send emails from HCD Portal"
            />
          </label>
        </SettingsCard>

        <SettingsCard title="What gets emailed" description="Once sending is on.">
          <ul className="grid gap-2 text-[15px]">
            <li>Asking for approval: every approver except the person asking.</li>
            <li>Approved, or sent back with a note: the person who asked.</li>
            <li>Sign and send: the proposal link to the client, if you tick the box.</li>
            <li>Client signs: a confirmation to the client, and a notice to HCD’s owners.</li>
            <li>Client completes onboarding: who to invoice, to HCD’s owners.</li>
          </ul>
        </SettingsCard>

        <SettingsCard title="Recent emails">
          {q.data.recent.length === 0 ? (
            <p className="text-[15px] text-muted-foreground">No emails yet.</p>
          ) : (
            <ul className="-my-2 divide-y">
              {q.data.recent.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{r.subject}</p>
                    <p className="truncate text-sm text-muted-foreground">
                      To {r.to} · {ukDate(r.sentAt ?? r.createdAt, true)}
                    </p>
                    {r.lastError && r.status !== 'sent' && (
                      <p className="text-[13px] text-destructive">{r.lastError}</p>
                    )}
                  </div>
                  <Badge variant="outline" className={STATUS[r.status][1]}>
                    {STATUS[r.status][0]}
                  </Badge>
                  {(r.status === 'dead' || r.status === 'failed') && (
                    <Button variant="outline" size="sm" onClick={() => retry.mutate(r.id)}>
                      <RotateCcw />
                      Try again
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </SettingsCard>
      </div>
    </>
  )
}
